import _sortBy from 'lodash/sortBy';
import { GL300_REGEX, GL500_REGEX } from 'src/services/constants';

/**
 * Returns all asset (non-vehicle) devices.
 *
 * @param {Object} state
 * @returns {Object[]}
 */
export function assetDevices(state) {
  return Object.values(state.devices).filter(({ serial }) => serial.startsWith('QT') || serial.startsWith('QW'));
}

/**
 * Returns all asset (non-vehicle) devices that match the current search filter
 *
 * @param {Object} state
 * @returns {Object[]}
 */
export function filteredAssetDevices(state, getters, rootState, rootGetters) {
  const assetsByDeviceKey = rootGetters['assets/assetsByDeviceKey'];
  const processedFilterTerms = rootGetters['search/processedFilterTerms'];
  return transformAndFilterDevices({
    assetsByDeviceKey,
    devs: getters.assetDevices,
    serialTest: (serial) => serial.startsWith('QT') || serial.startsWith('QW'),
    terms: processedFilterTerms,
  });
}

/**
 * Returns all dashcam/Raven devices that match the current search filter
 *
 * @param {Object} state
 * @returns {Object[]}
 */
export function filteredDashcamDevices(state, getters, rootState, rootGetters) {
  const assetsByDeviceKey = rootGetters['assets/assetsByDeviceKey'];
  const processedFilterTerms = rootGetters['search/processedFilterTerms'];
  return transformAndFilterDevices({
    assetsByDeviceKey,
    devs: getters.devices,
    serialTest: (serial) => serial.startsWith('1RV') || serial.startsWith('2RV'),
    terms: processedFilterTerms,
  });
}

/**
 * Returns all non-Raven, non-asset devices that match the current search filter
 *
 * @param {Object} state
 * @returns {Object[]}
 */
export function filteredObd2Devices(state, getters, rootState, rootGetters) {
  const assetsByDeviceKey = rootGetters['assets/assetsByDeviceKey'];
  const processedFilterTerms = rootGetters['search/processedFilterTerms'];
  return transformAndFilterDevices({
    assetsByDeviceKey,
    devs: getters.devices,
    serialTest: (serial) =>
      !serial.startsWith('QT') && !serial.startsWith('QW') && !serial.startsWith('1RV') && !serial.startsWith('2RV'),
    terms: processedFilterTerms,
  });
}

function transformAndFilterDevices({ assetsByDeviceKey = {}, devs = [], serialTest = () => true, terms = [] }) {
  return devs
    .map((device) => {
      const asset = assetsByDeviceKey[device.key];
      const { serial } = device;
      return {
        ...device,
        asset,
        assetName: asset?.nickname || '',
        obsolete: GL300_REGEX.test(serial) || GL500_REGEX.test(serial),
      };
    })
    .filter((device) => {
      const { serial } = device;
      const isCorrectType = serialTest(serial);
      if (!isCorrectType) {
        return false;
      }
      return terms.length === 0 || doesDeviceMatchFilter({ device, terms });
    });
}

function doesDeviceMatchFilter({ device = {}, terms = [] }) {
  const { assetName = '', serial = '', model = '' } = device;
  return [assetName.toLowerCase(), serial.toLowerCase(), model.toLowerCase()].some((property) => {
    const deviceProp = property.toLowerCase();
    return terms.some((term) => deviceProp.includes(term));
  });
}

export function devices(state) {
  const devs = Object.values(state.devices);
  return _sortBy(devs, 'serial');
}

export function devicesLoading(state) {
  return Boolean(state.devicesPromise);
}
